export function resizeImage(
  image,
  targetWidth,
  maxSize,
  originalFileName,
  type = "image/webp",
) {
  return new Promise((resolve, reject) => {
    if (!image) return reject("No image provided");

    const aspectRatio = image.width / image.height;

    let width = targetWidth;
    let height = Math.round(width / aspectRatio);

    // Ensure max size constraint
    if (width > maxSize || height > maxSize) {
      if (width > height) {
        width = maxSize;
        height = Math.round(width / aspectRatio);
      } else {
        height = maxSize;
        width = Math.round(height * aspectRatio);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return reject("Blob creation failed");

        const file = new File([blob], originalFileName, {
          type,
          lastModified: Date.now(),
        });

        resolve(file);
      },
      type,
      0.8,
    );
  });
}

// Function to convert a URL to a canvas, crop it, and return a new URL
function processImage(imageUrl, cropAmount) {
  return new Promise((resolve, reject) => {
    // Create an image element
    const img = new Image();

    // Handle load errors
    img.onerror = () => {
      reject(new Error("Failed to load the image"));
    };

    // When the image loads
    img.onload = () => {
      // Create a canvas element
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Calculate new dimensions after cropping
      const cropX = cropAmount;
      const cropY = cropAmount;
      const cropWidth = img.width - cropAmount * 2;
      const cropHeight = img.height - cropAmount * 2;

      // Set canvas dimensions to the cropped size
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      // Draw the cropped image on the canvas
      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropWidth,
        cropHeight, // Source rectangle
        0,
        0,
        cropWidth,
        cropHeight, // Destination rectangle
      );

      // Convert canvas to data URL
      const croppedUrl = canvas.toDataURL("image/png");
      resolve(croppedUrl);
    };

    // Set crossOrigin to handle CORS if needed
    img.crossOrigin = "Anonymous";

    // Set the source URL
    img.src = imageUrl;
  });
}

const getUserCookie = () =>
  `customerId=${encodeURIComponent(crypto.randomUUID())}`;

export function debounce(func = () => {}, delay = 0) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

export const getLogoDimentions = (imgHeight, imgWidth, box_dimension) => {
  const imageHeight = imgHeight;
  const imageWidth = imgWidth;

  const scale = Math.min(
    Number(box_dimension?.width || 100) / imageWidth,
    Number(box_dimension?.height || 100) / imageHeight,
  );

  // Get the new dimensions
  const newWidth = (imageWidth * scale).toFixed(0);
  const newHeight = (imageHeight * scale).toFixed(0);

  return {
    height: newHeight,
    width: newWidth,
  };
};

export function capitalizeFirstWord(sentence = null) {
  if (!sentence) return "";

  let words = sentence.trim().split("_");
  if (words.length === 0) return "";

  let firstWord = words[0];
  return firstWord.charAt(0).toUpperCase() + firstWord.slice(1);
}

export function groupShirtAreas(areas = []) {
  if (areas.length === 0) return;
  return areas?.reduce((acc, area) => {
    const parts = area.split("_");
    let group, transformed;

    if (parts.length === 2) {
      // Use the first part as the group key and swap the parts for the output
      group = parts[0];
      transformed = `${parts[0]}_${parts[1]}`;
    } else {
      // For areas without an underscore (e.g., "back")
      group = area;
      transformed = area;
    }

    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(transformed);
    return acc;
  }, {});
}

export function setCookie(
  daysToExpire,
  path = "/",
  secure = false,
  sameSite = "Lax",
) {
  let cookieString = getUserCookie();

  if (daysToExpire) {
    const date = new Date();
    date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  cookieString += `; path=${path}`;

  // Optional flags
  if (secure) cookieString += `; Secure`;
  if (sameSite) cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
}

export function retrieveCookie() {
  let cookies = document.cookie.split("; ");

  for (let cookie of cookies) {
    let [key, value] = cookie.split("=");
    if (key === "customerId") {
      return value;
    }
  }
  return null;
}

export function dataURLtoBlob(dataURL) {
  const byteString = atob(dataURL.split(",")[1]);
  const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uintArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uintArray[i] = byteString.charCodeAt(i);
  }

  return new Blob([uintArray], { type: mimeString });
}

export function readUploadFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;

      img.onload = async () => {
        try {
          const resizedFile = await resizeImage(img, 1500, 3000, file.name);
          resolve(resizedFile);
        } catch (error) {
          reject(error);
        }
      };
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

export function isCloseToWhite(color, threshold = 60) {
  // Create a temporary element to use the browser's color parsing
  const tempElement = document.createElement("div");
  document.body.appendChild(tempElement);

  // Set the color to get computed values
  tempElement.style.color = color;
  const computedColor = window.getComputedStyle(tempElement).color;

  // Remove the temporary element
  document.body.removeChild(tempElement);

  // Extract RGB values from computed color
  const rgbMatch = computedColor.match(/\d+/g);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch.map(Number);

    // Calculate distance from white (255,255,255)
    // Lower values mean closer to white
    const distanceFromWhite = 255 - r + (255 - g) + (255 - b);

    // Return true if the color is close enough to white based on threshold
    return distanceFromWhite < threshold;
  }

  // If we can't parse the color, return false
  return false;
}

// Usage example
export async function cropUploadedImg(url) {
  try {
    const cropAmount = 10; // Number of pixels to crop from each side
    const newUrl = await processImage(url, cropAmount);
    return newUrl;
  } catch (error) {
    console.error("Error processing image:", error);
  }
}

export function validateImage(imageFile, allowedFormats, maxSizeMB = 5) {
  // Check if a file was provided
  if (!imageFile) {
    console.error("No image file provided");
    return false;
  }

  // Check file size (convert MB to bytes: 1MB = 1,048,576 bytes)
  const maxSizeBytes = maxSizeMB * 1048576;
  if (imageFile.size > maxSizeBytes) {
    console.error(
      `Image size exceeds the maximum allowed size of ${maxSizeMB}MB`,
    );
    return { error: "size", isValid: false };
  }

  // Extract file extension from the file name
  const fileName = imageFile.name || "";
  const fileExtension = fileName.split(".").pop().toLowerCase();

  // Check if the file format is in the allowed formats list
  if (!allowedFormats.includes(fileExtension)) {
    console.error(
      `Image format '${fileExtension}' is not allowed. Allowed formats: ${allowedFormats.join(
        ", ",
      )}`,
    );

    return { error: "extension", isValid: false };
  }

  return { error: "", isValid: true };
}
